import React, { useState, useEffect, useMemo } from 'react';
import useModalHistory from '../hooks/useModalHistory';

const BadgeAchievementModal = ({ badges, onClose }) => {
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [showConfetti, setShowConfetti] = useState(true);
  
  // Handle browser back button
  const { closeModal } = useModalHistory(badges && badges.length > 0, onClose, 'badge-modal');

  // Pre-generate confetti data to avoid impure render calls
  const confettiParticles = useMemo(() => {
    const colors = ['#FFD700', '#FFA500', '#FF6347', '#00CED1', '#9370DB', '#32CD32'];
    return [...Array(30)].map((_, i) => ({
      id: i,
      left: `${(i * 3.33) % 100}%`,
      animationDelay: `${(i * 0.07) % 2}s`,
      animationDuration: `${2 + (i % 3)}s`,
      backgroundColor: colors[i % colors.length],
      rotation: `rotate(${(i * 12) % 360}deg)`,
    }));
  }, []);

  useEffect(() => {
    if (!badges || badges.length === 0) {
      closeModal?.();
      return;
    }

    // Auto-close after showing all badges
    const autoCloseTimer = setTimeout(() => {
      if (currentBadgeIndex < badges.length - 1) {
        setCurrentBadgeIndex(currentBadgeIndex + 1);
        setIsAnimating(true);
        setShowConfetti(true);
      } else {
        closeModal?.();
      }
    }, 4000);

    // Stop confetti after a bit
    const confettiTimer = setTimeout(() => {
      setShowConfetti(false);
    }, 2500);

    return () => {
      clearTimeout(autoCloseTimer);
      clearTimeout(confettiTimer);
    };
  }, [badges, currentBadgeIndex, closeModal]);

  if (!badges || badges.length === 0) return null;

  const currentBadge = badges[currentBadgeIndex];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      {/* Confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confettiParticles.map((particle) => (
            <div
              key={particle.id}
              className="absolute animate-confetti"
              style={{
                left: particle.left,
                top: '-20px',
                animationDelay: particle.animationDelay,
                animationDuration: particle.animationDuration,
              }}
            >
              <div
                className="w-3 h-3 rounded-sm"
                style={{
                  backgroundColor: particle.backgroundColor,
                  transform: particle.rotation,
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Radial glow effect */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-96 h-96 bg-amber-500/30 rounded-full blur-3xl animate-pulse" />
      </div>

      {/* Badge card */}
      <div 
        className={`relative bg-gradient-to-br from-[#1a1a1f] to-[#0f0f12] border-2 border-amber-500/50 rounded-3xl p-8 max-w-sm mx-4 shadow-2xl shadow-amber-500/20 transform transition-all duration-700 ${
          isAnimating ? 'scale-100 opacity-100' : 'scale-90 opacity-0'
        }`}
        onClick={() => {
          if (currentBadgeIndex < badges.length - 1) {
            setCurrentBadgeIndex(currentBadgeIndex + 1);
            setIsAnimating(true);
            setShowConfetti(true);
          } else {
            closeModal?.();
          }
        }}
      >
        {/* Shine effect */}
        <div className="absolute inset-0 rounded-3xl overflow-hidden">
          <div className="absolute -inset-full bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shine" />
        </div>

        {/* Badge counter */}
        {badges.length > 1 && (
          <div className="absolute top-4 right-4 text-amber-400/60 text-sm">
            {currentBadgeIndex + 1} / {badges.length}
          </div>
        )}

        {/* Achievement header */}
        <div className="text-center mb-4">
          <div className="text-amber-400 text-sm font-medium tracking-widest uppercase mb-2 animate-pulse">
            🎉 Achievement Unlocked! 🎉
          </div>
        </div>

        {/* Badge icon with animation */}
        <div className="relative flex justify-center mb-6">
          <div className="relative">
            {/* Outer glow ring */}
            <div className="absolute inset-0 -m-4 bg-amber-500/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
            
            {/* Inner rotating ring */}
            <div className="absolute inset-0 -m-2 border-2 border-amber-500/30 rounded-full animate-spin" style={{ animationDuration: '8s' }} />
            
            {/* Badge emoji */}
            <div className="text-8xl animate-bounce" style={{ animationDuration: '1s' }}>
              {currentBadge.icon}
            </div>
          </div>
        </div>

        {/* Badge name */}
        <h2 className="text-3xl font-bold text-center text-white mb-2 playfair">
          {currentBadge.name}
        </h2>

        {/* Badge description */}
        <p className="text-center text-white/70 text-lg mb-6">
          {currentBadge.description}
        </p>

        {/* Tap to continue hint */}
        <div className="text-center text-white/40 text-sm animate-pulse">
          Tap to continue
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
        @keyframes shine {
          0% {
            transform: translateX(-100%) rotate(25deg);
          }
          100% {
            transform: translateX(200%) rotate(25deg);
          }
        }
        .animate-shine {
          animation: shine 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default BadgeAchievementModal;
